import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Lock, Check } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }
    if (pwd !== pwd2) {
      toast.error("Les deux mots de passe ne correspondent pas");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      toast.success("Mot de passe mis à jour");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-12 bg-[#FFFFFF]">
      <Card className="w-full max-w-md card-soft border-0">
        <CardContent className="p-7 sm:p-10">
          <Link
            to="/login"
            data-testid="reset-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-[#6b7f79] hover:text-[#2D5951] mb-5"
          >
            <ArrowLeft className="w-4 h-4" /> Retour à la connexion
          </Link>

          <div className="flex items-center gap-3 mb-7">
            <img
              src="/logo-cantine.png"
              alt="La Cantine de Géraldine"
              className="h-14 w-auto object-contain"
              data-testid="reset-logo"
            />
          </div>

          {!token ? (
            <div data-testid="reset-missing-token">
              <h2 className="font-display text-2xl text-[#1A2B27] mb-2">Lien invalide</h2>
              <p className="text-sm text-[#6b7f79]">
                Le lien de réinitialisation est incomplet. Refais une demande depuis l'écran de connexion.
              </p>
              <Link to="/forgot-password">
                <Button className="mt-6 w-full h-12 rounded-full bg-[#2D5951] hover:bg-[#1F423C] text-white font-semibold">
                  Demander un nouveau lien
                </Button>
              </Link>
            </div>
          ) : done ? (
            <div data-testid="reset-done">
              <h2 className="font-display text-2xl text-[#1A2B27] mb-2 flex items-center gap-2">
                <Check className="w-6 h-6 text-[#388E3C]" /> Mot de passe mis à jour
              </h2>
              <p className="text-sm text-[#6b7f79]">Tu vas être redirigé vers la page de connexion…</p>
            </div>
          ) : (
            <>
              <h2 className="font-display text-2xl text-[#1A2B27] mb-1 flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#2D5951]" /> Nouveau mot de passe
              </h2>
              <p className="text-sm text-[#6b7f79] mb-6">
                Choisis un mot de passe d'au moins <b>8 caractères</b>.
              </p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <Label htmlFor="pwd" className="label-tiny">Nouveau mot de passe</Label>
                  <Input
                    id="pwd"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    data-testid="reset-password-input"
                    className="mt-2 h-12 rounded-xl border-[#DDE7E4] focus-visible:ring-[#2D5951]"
                  />
                </div>
                <div>
                  <Label htmlFor="pwd2" className="label-tiny">Confirmation</Label>
                  <Input
                    id="pwd2"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={pwd2}
                    onChange={(e) => setPwd2(e.target.value)}
                    data-testid="reset-password-confirm"
                    className="mt-2 h-12 rounded-xl border-[#DDE7E4] focus-visible:ring-[#2D5951]"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="reset-submit-button"
                  className="w-full h-12 rounded-full bg-[#2D5951] hover:bg-[#1F423C] text-white font-semibold tracking-wide hover:-translate-y-0.5 shadow-md"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Mettre à jour"}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
